import { useState } from 'react';
import { MoreVertical, Flag, UserX } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ReportMessageDialog } from './report-message-dialog';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { useToast } from '@/components/ui/use-toast';
import type { Database } from '@/types/supabase';

type ChatSource = Database['public']['Enums']['chat_source'];

interface ChatMessageMenuProps {
  messageId: string;
  authorId: string;
  authorName: string;
  source: ChatSource;
  onBlocked?: (blockedUserId: string) => void;
}

export function ChatMessageMenu({
  messageId,
  authorId,
  authorName,
  source,
  onBlocked,
}: ChatMessageMenuProps) {
  const { profile } = useProfile();
  const { toast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const doBlock = async () => {
    if (!profile?.id || profile.id === authorId) return;
    setBlocking(true);
    const { error } = await supabase.from('chat_blocks').insert({
      blocker_id: profile.id,
      blocked_id: authorId,
    });
    setBlocking(false);
    setBlockOpen(false);
    if (error) {
      console.error('Block failed:', error);
      toast({
        variant: 'destructive',
        title: 'Blokkeren mislukt',
        description: 'Probeer het opnieuw.',
      });
      return;
    }
    toast({
      title: `${authorName} geblokkeerd`,
      description: 'Je ziet berichten van deze persoon niet meer.',
    });
    onBlocked?.(authorId);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Bericht opties"
            className="[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/msg:opacity-100 [@media(hover:hover)]:focus:opacity-100 transition-opacity text-muted-foreground hover:text-foreground p-1"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setReportOpen(true)}>
            <Flag className="mr-2 h-4 w-4" />
            Rapporteer bericht
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setBlockOpen(true)}
            className="text-red-600 focus:text-red-600"
          >
            <UserX className="mr-2 h-4 w-4" />
            Blokkeer {authorName}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ReportMessageDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        messageId={messageId}
        reportedUserId={authorId}
        source={source}
      />

      <AlertDialog
        open={blockOpen}
        onOpenChange={(v) => !blocking && setBlockOpen(v)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{authorName} blokkeren?</AlertDialogTitle>
            <AlertDialogDescription>
              Je ziet geen berichten meer van deze persoon in chats. Je kunt de
              blokkade later opheffen via je profiel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={blocking}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBlock}
              disabled={blocking}
              className="bg-red-600 hover:bg-red-700"
            >
              {blocking ? 'Bezig...' : 'Blokkeer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
